import { Formik } from 'formik'
import React from 'react'

import { Dashboard, DashboardBackButton, PageLayout } from '@components'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  ButtonClean,
  Dropdown,
  HeadContent,
  Loading,
  SaveShortcutListener,
  ToolbarButton,
  useMediaQueryContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon } from '@fourthwall/icons'
import { routing } from '@utils/routing'

import { ConfirmBar } from '../../../../modules/ConfirmBar'
import { GiftCardStatusTag } from '../../components/GiftCardStatusTag/GiftCardStatusTag'
import * as S from './GiftCard.styled'
import { validationSchema } from './consts'
import { useGiftCard } from './hooks/useGiftCard'
import { GiftCardDetails } from './modules/GiftCardDetails/GiftCardDetails'
import { GiftCardShareBanner } from './modules/GiftCardShareBanner/GiftCardShareBanner'
import { GiftCardSummary } from './modules/GiftCardSummary'
import { GiftCardTimeline } from './modules/GiftCardTimeline/GiftCardTimeline'
import { getGiftCardTitle } from './utils/getGiftCardTitle'
import { isGiftCardGroup } from './utils/isGiftCardGroup'

export const GiftCard = () => {
  const media = useMediaQueryContext()
  const {
    giftCard,
    timelineEvents,
    initialValues,
    isCreating,
    isCreatedFromOrder,
    isSubmitting,
    isFetching,
    onSubmit,
    onDeactivate,
    onReactivate,
    onArchive,
    onCreateGiftCardProduct,
  } = useGiftCard()
  const isFormLoading = isSubmitting || isFetching

  const getPageTitle = (): string => {
    if (isCreating) {
      return 'Create gift card'
    }

    return giftCard ? getGiftCardTitle(giftCard) : ''
  }

  const leftContent = (
    <>
      <GiftCardDetails
        disabled={!isCreating}
        giftCards={giftCard && isGiftCardGroup(giftCard) ? giftCard.giftCards : []}
      />
      {!!timelineEvents.length && <GiftCardTimeline timelineEvents={timelineEvents} />}
    </>
  )

  const renderToolbar = () => {
    if (!onDeactivate && !onReactivate && !onArchive) {
      return null
    }

    return (
      <Dropdown
        content={
          <>
            {onDeactivate && <Dropdown.Item label="Disable" onClick={onDeactivate} />}
            {onReactivate && <Dropdown.Item label="Enable" onClick={onReactivate} />}
            {onArchive && <Dropdown.Item label="Delete" onClick={onArchive} />}
          </>
        }
        position="bottom-end"
      >
        <ToolbarButton
          label="Settings"
          icon={DotsHorizontalIcon}
          size={media.tablet ? 'medium' : 'small'}
        />
      </Dropdown>
    )
  }

  const renderStatus = () => {
    if (isCreating || !giftCard) {
      return null
    }

    return <GiftCardStatusTag status={giftCard.status} size="large" />
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ dirty, submitForm }) => (
        <>
          <HeadContent title={getPageTitle()} />
          <Loading isLoading={isFormLoading}>
            <PageLayout
              rightContent={<GiftCardSummary giftCard={giftCard} />}
              leftContent={leftContent}
            >
              <Dashboard.ItemViewHeader
                name={getPageTitle()}
                title={
                  isCreating ? (
                    <S.Info>
                      Looking to sell gift cards on your shop?{' '}
                      <ButtonClean
                        label="Create gift card product"
                        appearance="secondary"
                        onClick={onCreateGiftCardProduct}
                      />{' '}
                      instead of promotion.
                    </S.Info>
                  ) : undefined
                }
                status={renderStatus()}
                toolbar={renderToolbar()}
                backButton={
                  <DashboardBackButton label="Promotions" to={routing.promotions.giftCards.self} />
                }
              />
              {giftCard && !isCreatedFromOrder && <GiftCardShareBanner giftCard={giftCard} />}
            </PageLayout>
          </Loading>
          <SaveShortcutListener disabled={!dirty} onSave={submitForm} />
          <ConfirmBar isOpen={dirty} isLoading={isSubmitting} onConfirm={submitForm} />
          <LeavingGuard when={dirty} />
        </>
      )}
    </Formik>
  )
}
